// Pure math behind the token history chart: series extraction, scales, SVG
// paths, hover lookup, axis ticks and labels. No DOM, so it is unit-tested
// directly (history-chart.test.ts). Values are drawn as observed: a null is
// dropped from the line, never interpolated or filled.

import { amount, compactUsd, price, wholeUsd } from "../format";
import type { HistoryPoint, TokenHistory } from "../token-api";

export type SeriesKey = "price" | "market_cap" | "supply";
export type ValueKind = "price" | "usd" | "amount";

export interface SeriesSpec {
  key: SeriesKey;
  label: string;
  kind: ValueKind;
}

export const SERIES: readonly SeriesSpec[] = [
  { key: "price", label: "Price", kind: "price" },
  { key: "market_cap", label: "Market cap", kind: "usd" },
  { key: "supply", label: "Supply", kind: "amount" },
];

export const seriesSpec = (key: SeriesKey): SeriesSpec => SERIES.find((s) => s.key === key) ?? SERIES[0]!;

export interface ChartPoint {
  /** Milliseconds since the epoch (the bucket's last observation slot). */
  t: number;
  v: number;
}

/** Points with a finite value, in slot order. Nulls are dropped, never filled. */
export function seriesPoints(series: readonly HistoryPoint[], key: SeriesKey): ChartPoint[] {
  const out: ChartPoint[] = [];
  for (const point of series) {
    const v = point[key];
    const t = Date.parse(point.slot);
    if (typeof v === "number" && Number.isFinite(v) && Number.isFinite(t)) out.push({ t, v });
  }
  return out;
}

/** Pixel box of the chart and the inner margins the line keeps clear of. */
export interface Frame {
  width: number;
  height: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Scale {
  frame: Frame;
  /** Data extent. */
  t0: number;
  t1: number;
  min: number;
  max: number;
  /** Value domain mapped onto the inner height (widened for a flat series). */
  lo: number;
  hi: number;
  x(t: number): number;
  y(v: number): number;
  /** Inverse of x, clamped to the data extent. */
  t(x: number): number;
}

/** Linear scales for a non-empty series. A flat series is centred, not pinned to the floor. */
export function chartScale(points: readonly ChartPoint[], frame: Frame): Scale {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) throw new Error("chartScale needs at least one point");
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const { v } of points) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  let lo = min;
  let hi = max;
  if (hi === lo) {
    const pad = Math.abs(min) * 0.005 || 1;
    lo -= pad;
    hi += pad;
  }
  const t0 = first.t;
  const t1 = last.t;
  const spanT = Math.max(1, t1 - t0);
  const innerW = Math.max(1, frame.width - frame.left - frame.right);
  const innerH = Math.max(1, frame.height - frame.top - frame.bottom);
  return {
    frame,
    t0,
    t1,
    min,
    max,
    lo,
    hi,
    x: (t) => frame.left + ((t - t0) / spanT) * innerW,
    y: (v) => frame.top + ((hi - v) / (hi - lo)) * innerH,
    t: (x) => t0 + Math.min(1, Math.max(0, (x - frame.left) / innerW)) * spanT,
  };
}

const px = (n: number) => n.toFixed(1);

/** `M x,y L x,y …` through every point. */
export function linePath(points: readonly ChartPoint[], scale: Scale): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${px(scale.x(p.t))},${px(scale.y(p.v))}`).join(" ");
}

/** The line closed down to the chart floor, for a soft fill under it. */
export function areaPath(points: readonly ChartPoint[], scale: Scale): string {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return "";
  const floor = px(scale.frame.height - scale.frame.bottom);
  return `${linePath(points, scale)} L${px(scale.x(last.t))},${floor} L${px(scale.x(first.t))},${floor} Z`;
}

/** Index of the point closest in time to `t`; points must be sorted by t. */
export function nearestIndex(points: readonly ChartPoint[], t: number): number {
  if (points.length === 0) return -1;
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]!.t < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && t - points[lo - 1]!.t <= points[lo]!.t - t) return lo - 1;
  return lo;
}

/** Indices of the lowest and highest values (first occurrence of each). */
export function extremes(points: readonly ChartPoint[]): { min: number; max: number } {
  let min = 0;
  let max = 0;
  points.forEach((p, i) => {
    if (p.v < points[min]!.v) min = i;
    if (p.v > points[max]!.v) max = i;
  });
  return { min, max };
}

/** Text anchor that keeps a label at x inside a box of the given width. */
export function labelAnchor(x: number, width: number, margin = 0.18): "start" | "middle" | "end" {
  if (x < width * margin) return "start";
  if (x > width * (1 - margin)) return "end";
  return "middle";
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Candidate tick steps, all UTC-aligned when applied with Math.ceil(t / step) * step. */
const STEPS = [30 * MINUTE, HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 2 * DAY, 7 * DAY, 14 * DAY, 28 * DAY, 91 * DAY, 364 * DAY];

/** Evenly spaced, UTC-aligned tick times inside [t0, t1], at most `maxTicks` of them. */
export function timeTicks(t0: number, t1: number, maxTicks: number): { step: number; ticks: number[] } {
  const span = Math.max(0, t1 - t0);
  const step = STEPS.find((s) => span / s <= Math.max(1, maxTicks)) ?? STEPS[STEPS.length - 1]!;
  const ticks: number[] = [];
  for (let t = Math.ceil(t0 / step) * step; t <= t1; t += step) ticks.push(t);
  return { step, ticks };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const two = (n: number) => String(n).padStart(2, "0");

/** Short UTC axis label: clock time for sub-day steps, otherwise month and day. */
export function tickLabel(t: number, step: number): string {
  const d = new Date(t);
  if (step < DAY) return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}`;
  const day = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return step >= 91 * DAY ? `${day} ${d.getUTCFullYear()}` : day;
}

/** Full UTC stamp for a point: `2026-10-06 18:30 UTC`. */
export function timeLabel(t: number): string {
  const iso = new Date(t).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

const compactNumber = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 });

/** Axis-friendly label by default; `exact` for the hover readout. */
export function valueLabel(v: number, kind: ValueKind, exact = false): string {
  switch (kind) {
    case "price":
      return price(v);
    case "usd":
      return exact ? wholeUsd(v) : compactUsd(v);
    case "amount":
      return exact || Math.abs(v) < 10_000 ? amount(v) : compactNumber.format(v);
  }
}

/** `41 points · interval 30m · last observation per bucket`, plus how many buckets lacked this value. */
export function chartCaption(history: Pick<TokenHistory, "points" | "interval">, drawn: number): string {
  const base = `${history.points} ${history.points === 1 ? "point" : "points"} · interval ${history.interval} · last observation per bucket`;
  const missing = history.points - drawn;
  return missing > 0 ? `${base} · ${missing} without a value` : base;
}
